/* eslint-disable @angular-eslint/prefer-inject */
/* eslint-disable no-empty */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  OnInit,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  OnDestroy,
  inject,
} from '@angular/core';
import {
  LucideAngularModule,
  Bell,
  X,
  CheckCircle,
  Info,
  AlertCircle,
  AlertTriangle,
  BellRing,
  BellOff,
} from 'lucide-angular';
import {
  NotificationService,
  VAULT_BACKEND_NOTIFICATIONS,
  type VaultBackendNotificationRow,
} from '@nebula/shared-vault/data-access';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export interface Notificacion {
  id: string;
  titulo: string;
  mensaje: string;
  fecha: Date;
  leida: boolean;
  tipo: 'success' | 'info' | 'warning' | 'error';
}

@Component({
  selector: 'app-notificacion',
  templateUrl: './notificacion.component.html',
  styleUrls: ['./notificacion.component.scss'],
  standalone: true,
  imports: [CommonModule, LucideAngularModule],
})
export class NotificacionComponent implements OnInit, OnDestroy {
  @Input() notificaciones: Notificacion[] = [];
  @Input() modoOscuro = false;
  @Output() notificacionLeida = new EventEmitter<string>();
  @Output() marcarTodasLeidasEvent = new EventEmitter<void>();
  @Output() cerrarPanel = new EventEmitter<void>();
  @Output() togglePushNotifications = new EventEmitter<boolean>();

  private readonly notificationService = inject(NotificationService);
  private readonly backendNotifications = inject(VAULT_BACKEND_NOTIFICATIONS, { optional: true });
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();

  readonly icons = {
    Bell,
    X,
    CheckCircle,
    Info,
    AlertCircle,
    AlertTriangle,
    BellRing,
    BellOff,
  };

  maxNotificacionesVisibles = 3;
  notificacionesPushActivas = false;
  sincronizando = false;
  private subscription: Subscription | null = null;
  private notificacionSubscription: Subscription | null = null;
  private pollingInterval: ReturnType<typeof setInterval> | null = null;
  private backendPollInterval: ReturnType<typeof setInterval> | null = null;

  ngOnInit() {
    // El tema lo define el layout (`[isDarkMode]` → `modoOscuro`). No usar `prefers-color-scheme`
    // aquí: en modo claro de la app pero OS en oscuro, el panel quedaba oscuro por error.

    this.cargarNotificacionesGuardadas();
    if (this.backendNotifications) {
      this.refreshFromBackend();
    }
    this.cargarPreferenciaPush();
    this.setupPushNotifications();

    this.notificacionSubscription = this.notificationService.onNotificacionRecibida
      .pipe(takeUntil(this.destroy$))
      .subscribe((notificacion) => {
        const nuevaNotificacion: Notificacion = {
          id: notificacion.id || Date.now().toString(),
          titulo: notificacion.titulo,
          mensaje: notificacion.mensaje,
          fecha: new Date(),
          leida: false,
          tipo: notificacion.tipo || 'info',
        };

        this.agregarNotificacion(nuevaNotificacion);
        this.mostrarToast(nuevaNotificacion.titulo, nuevaNotificacion.mensaje);
      });

    this.setupServiceWorkerListener();

    setTimeout(() => {
      this.sincronizarPendientes();
      this.iniciarPollingAuto();
    }, 2000);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.notificacionSubscription) {
      this.notificacionSubscription.unsubscribe();
    }
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
    }
    if (this.backendPollInterval) {
      clearInterval(this.backendPollInterval);
    }
  }

  private setupServiceWorkerListener() {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data && event.data.type === 'NOTIFICATION_CLICKED') {
          const notif = event.data.notification;

          const nuevaNotificacion: Notificacion = {
            id: notif.id || Date.now().toString(),
            titulo: notif.titulo || 'Notificación OneSignal',
            mensaje: notif.mensaje || '',
            fecha: new Date(notif.fecha || new Date()),
            leida: false,
            tipo: 'info',
          };

          this.agregarNotificacion(nuevaNotificacion);
          this.mostrarToast(nuevaNotificacion.titulo, nuevaNotificacion.mensaje);
        }

        if (event.data && event.data.type === 'PENDING_NOTIFICATIONS') {
          const notificaciones = event.data.notifications;

          notificaciones.forEach((notif: any) => {
            const yaExiste = this.notificaciones.some((n) => n.id === notif.id);
            if (!yaExiste) {
              const nuevaNotificacion: Notificacion = {
                id: notif.id || Date.now().toString(),
                titulo: notif.titulo || 'Notificación',
                mensaje: notif.mensaje || '',
                fecha: new Date(notif.fecha || new Date()),
                leida: false,
                tipo: 'info',
              };
              this.agregarNotificacion(nuevaNotificacion);
            }
          });
        }
      });
    }
  }

  private mostrarToast(titulo: string, mensaje: string) {
    const toast = document.createElement('div');
    toast.style.cssText = `
      position: fixed;
      bottom: 20px;
      right: 20px;
      background: #4CAF50;
      color: white;
      padding: 12px 20px;
      border-radius: 8px;
      z-index: 10000;
      font-family: sans-serif;
      box-shadow: 0 2px 10px rgba(0,0,0,0.2);
      animation: slideIn 0.3s ease;
    `;
    toast.innerHTML = `
      <strong>🔔 ${titulo}</strong><br>
      ${mensaje.substring(0, 50)}<br>
      <small>✅ Notificación guardada en el panel</small>
    `;
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 3000);
  }

  private cargarNotificacionesGuardadas() {
    const guardadas = localStorage.getItem('notificaciones_reales');
    if (guardadas) {
      const notificacionesGuardadas = JSON.parse(guardadas);
      this.notificaciones = notificacionesGuardadas.map((n: any) => ({
        ...n,
        fecha: new Date(n.fecha),
      }));
    }
  }

  private guardarNotificaciones() {
    const notificacionesAGuardar = this.notificaciones.slice(0, 100);
    localStorage.setItem('notificaciones_reales', JSON.stringify(notificacionesAGuardar));
  }

  private refreshFromBackend(): void {
    const api = this.backendNotifications;
    if (!api) return;
    api
      .list(false)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (rows) => {
          const fromApi = rows.map((r) => this.mapBackendRow(r));
          this.notificaciones = this.mergeApiWithLocal(fromApi);
          this.guardarNotificaciones();
          this.cdr.markForCheck();
        },
        error: () => {},
      });
  }

  private mapBackendRow(row: VaultBackendNotificationRow): Notificacion {
    return {
      id: row.id,
      titulo: row.titulo,
      mensaje: row.mensaje,
      fecha: new Date(row.fecha),
      leida: row.leida,
      tipo: row.tipo,
    };
  }

  /** API como fuente de verdad; conserva entradas solo locales (p. ej. push aún no persistidas). */
  private mergeApiWithLocal(fromApi: Notificacion[]): Notificacion[] {
    const apiIds = new Set(fromApi.map((n) => n.id));
    const localOnly = this.notificaciones.filter((n) => !apiIds.has(n.id));
    return [...fromApi, ...localOnly].sort(
      (a, b) => b.fecha.getTime() - a.fecha.getTime(),
    );
  }

  cargarPreferenciaPush() {
    const savedPreference = localStorage.getItem('notificaciones_push_activas');
    if (savedPreference !== null) {
      this.notificacionesPushActivas = savedPreference === 'true';
    }
  }

  private async setupPushNotifications() {
    try {
      const isSubscribed = await this.notificationService.isSubscribed();
      this.notificacionesPushActivas = isSubscribed;
      localStorage.setItem('notificaciones_push_activas', String(isSubscribed));
      this.cdr.detectChanges();

      this.subscription = this.notificationService
        .getSubscriptionStatus()
        .subscribe((isSubscribed) => {
          setTimeout(() => {
            this.notificacionesPushActivas = isSubscribed;
            localStorage.setItem('notificaciones_push_activas', String(isSubscribed));
            this.togglePushNotifications.emit(isSubscribed);
            this.cdr.detectChanges();
          });
        });
    } catch (error) {}
  }

  async toggleNotificacionesPushManual() {
    this.notificacionesPushActivas = !this.notificacionesPushActivas;
    await this.handlePushToggle(this.notificacionesPushActivas);
  }

  async toggleNotificacionesPush(event: Event) {
    const input = event.target as HTMLInputElement;
    const newState = input.checked;
    this.notificacionesPushActivas = newState;
    await this.handlePushToggle(newState);
  }

  private async handlePushToggle(activate: boolean) {
    try {
      if (activate) {
        const success = await this.notificationService.subscribe();
        if (!success) {
          this.notificacionesPushActivas = false;
        }
      } else {
        await this.notificationService.unsubscribe();
      }
      this.guardarYEmitirEstado();
    } catch (error) {
      this.notificacionesPushActivas = !activate;
    } finally {
      this.cdr.detectChanges();
    }
  }

  private guardarYEmitirEstado() {
    localStorage.setItem('notificaciones_push_activas', String(this.notificacionesPushActivas));
    this.togglePushNotifications.emit(this.notificacionesPushActivas);
  }

  enviarNotificacionPushNavegador(notificacion: Notificacion) {
    if (!this.notificacionesPushActivas) return;

    if (!('Notification' in window)) {
      return;
    }

    if (Notification.permission !== 'granted') {
      return;
    }

    try {
      new Notification(notificacion.titulo, {
        body: notificacion.mensaje,
        icon: '/assets/icon-notification.png',
        silent: false,
        tag: `notification-${notificacion.id}`,
      });
    } catch (error) {}
  }

  agregarNotificacion(notificacion: Notificacion) {
    const yaExiste = this.notificaciones.some((n) => n.id === notificacion.id);
    if (yaExiste) {
      return;
    }

    this.notificaciones.unshift(notificacion);
    this.guardarNotificaciones();
    this.cdr.detectChanges();

    if (this.notificacionesPushActivas) {
      this.enviarNotificacionPushNavegador(notificacion);
    }
  }

  async sincronizarPendientes() {
    if ('serviceWorker' in navigator) {
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration && registration.active) {
        registration.active.postMessage({ type: 'GET_PENDING_NOTIFICATIONS' });
      }
    }
  }

  iniciarPollingAuto() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
    }

    this.pollingInterval = setInterval(() => {
      this.sincronizarPendientes();
    }, 30000);

    if (this.backendNotifications) {
      if (this.backendPollInterval) {
        clearInterval(this.backendPollInterval);
      }
      // Misma cadencia que el header del portal Next (`frontend`): 60s
      this.backendPollInterval = setInterval(() => this.refreshFromBackend(), 60000);
    }
  }

  async sincronizarNotificaciones() {
    this.sincronizando = true;
    try {
      await this.sincronizarPendientes();
      if (this.backendNotifications) {
        this.refreshFromBackend();
      }

      setTimeout(() => {
        this.sincronizando = false;
        this.cdr.detectChanges();
      }, 1000);
    } catch (error) {
      this.sincronizando = false;
    }
  }

  marcarComoLeida(id: string) {
    const notificacion = this.notificaciones.find((n) => n.id === id);
    if (!notificacion || notificacion.leida) {
      return;
    }

    const applyLocal = () => {
      notificacion.leida = true;
      this.guardarNotificaciones();
      this.notificacionLeida.emit(id);
      this.cdr.detectChanges();
    };

    const api = this.backendNotifications;
    if (api) {
      api
        .markAsRead(id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => applyLocal(),
          error: () => applyLocal(),
        });
    } else {
      applyLocal();
    }
  }

  marcarTodasComoLeidas() {
    const api = this.backendNotifications;
    const applyLocal = () => {
      let hayCambios = false;
      this.notificaciones.forEach((notif) => {
        if (!notif.leida) {
          notif.leida = true;
          hayCambios = true;
        }
      });
      if (hayCambios) {
        this.guardarNotificaciones();
        this.marcarTodasLeidasEvent.emit();
        this.cdr.detectChanges();
      }
    };

    if (api) {
      api
        .markAllAsRead()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => applyLocal(),
          error: () => applyLocal(),
        });
    } else {
      applyLocal();
    }
  }

  getUnreadCount(): number {
    return this.notificaciones.filter((n) => !n.leida).length;
  }

  eliminarNotificacion(id: string) {
    this.notificaciones = this.notificaciones.filter((n) => n.id !== id);
    this.guardarNotificaciones();
    this.cdr.detectChanges();
  }

  getIconoPorTipo(tipo: string): any {
    const iconos = {
      success: this.icons.CheckCircle,
      info: this.icons.Info,
      warning: this.icons.AlertTriangle,
      error: this.icons.AlertCircle,
    };
    return iconos[tipo as keyof typeof iconos] || this.icons.Info;
  }

  truncarTexto(texto: string, maxLength = 250): string {
    if (!texto) return '';
    if (texto.length <= maxLength) return texto;
    return texto.substring(0, maxLength) + '...';
  }

  formatFecha(fecha: Date): string {
    const dia = fecha.getDate().toString().padStart(2, '0');
    const mes = (fecha.getMonth() + 1).toString().padStart(2, '0');
    const año = fecha.getFullYear();
    const horas = fecha.getHours().toString().padStart(2, '0');
    const minutos = fecha.getMinutes().toString().padStart(2, '0');
    const segundos = fecha.getSeconds().toString().padStart(2, '0');
    return `${dia}/${mes}/${año} ${horas}:${minutos}:${segundos}`;
  }

  cerrar() {
    this.cerrarPanel.emit();
  }
}
